/* eslint-disable security/detect-object-injection */
import React, { useCallback, useMemo } from 'react';
import _ from 'lodash';
import { wizardProps, WizardPage } from '@xengage/gw-portals-wizard-react';
import { ViewModelForm, withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { PremierConstants } from 'premier-constant-service';
import { useUpdateSubmissionServices } from 'premier-capability-hooks';
import AdditionalInterestComponent from "premier-capability-policyjob-react/components/AddllInterestComponent/AddlInterestComponent.jsx"
import { useTranslator } from '@jutro/locale';
import styles from '../../SpecialtyPersonalStyles.module.scss'
import messages from "./LocRiskCoveragesPage.messages.js";
import presentationMetadata from "./LocRiskCoveragesPage.metadata.json5";
import CoveragesPageComponent from "../Coverages/CoveragesPage";

function LocRiskCoveragesPage(props) {
    const {
        wizardData: submissionVM, updateWizardData, currentStep, callbackMap
    } = props;
    const { pageKey } = currentStep;
    const metadata = presentationMetadata[pageKey];
    const { initialValidation, onValidate } = useValidation('LocRiskCoveragesPage');
    const { updateSubmission } = useUpdateSubmissionServices();
    const { lineName } = PremierConstants;
    const offeringCode = _.get(submissionVM.value, 'lobData.perLine.selectedOffering_Ext')
    const translator = useTranslator();

    const onNext = useCallback(
        async () => {

            const { productCode } = submissionVM.value.baseData;
            const lobLineName = lineName[productCode];

            submissionVM.value = await updateSubmission(lobLineName, 'PropertyCoveragesPage', submissionVM);

            return submissionVM;
        },
        [lineName, submissionVM, updateSubmission]
    );

    const getPathUpdated = useCallback(() => {
        let offeringPath = ''

        switch (offeringCode) {
            case 'Cond':
                offeringPath = 'lobData.perLine.coverables.condoCoverable.condominiums'
                break;

            case 'RisAssDwe':
                offeringPath = 'lobData.perLine.coverables.riskAssistDwellingsCoverable.riskAssistDwellings'
                break;

            case 'PerProInSto':
                offeringPath = 'lobData.perLine.coverables.propertyStorageCoverable.propertyInStorage'
                break;

            case 'MobHom':
                offeringPath = 'lobData.perLine.coverables.mobileHomeCoverable.mobileHomes'
                break;

            case 'TinHom':
                offeringPath = 'lobData.perLine.coverables.tinyHomesCoverable.tinyHomes'
                break;
            case 'PerUmbLia':
                offeringPath = 'lobData.perLine.coverables.perUmbLiaCoverable'
                break;
            default:
                offeringPath = ''
        }

        return offeringPath

    }, [offeringCode])

    const offPath = getPathUpdated()

    const getProInStoAccordianTitle = useCallback((index) => (
        <div className={styles.accordianTitle}>
            <p>
                {`${translator(messages.stoFacNamValue)}${_.get(submissionVM.value, `lobData.perLine.coverables.propertyStorageCoverable.propertyInStorage[${index}].propertyDetails.locRisk.stoFacFie`)
                    }`}
            </p>
            <p>
                {`${translator(messages.stoFacLocValue)}${_.get(submissionVM.value, `lobData.perLine.coverables.propertyStorageCoverable.propertyInStorage[${index}].propertyDetails.locRisk.stoAddFie`)
                    }`}
            </p>
        </div>
    ), [submissionVM, translator]);

    const getTitleofAccordion = useCallback((index) => {
        if (offeringCode === 'Cond') {
            return _.get(submissionVM.value, `lobData.perLine.coverables.condoCoverable.condominiums[${index}].propertyDetails.accountLocFie.displayName`)
        }

        if (offeringCode === "MobHom") {
            return _.get(submissionVM.value, `lobData.perLine.coverables.mobileHomeCoverable.mobileHomes[${index}].propertyDetails.accountLocFie.displayName`);
        }

        if (offeringCode === "RisAssDwe") {
            return _.get(submissionVM.value, `lobData.perLine.coverables.riskAssistDwellingsCoverable.riskAssistDwellings[${index}].propertyDetails.accountLocFie.displayName`);
        }

        if (offeringCode === "TinHom") {
            return _.get(submissionVM.value, `lobData.perLine.coverables.tinyHomesCoverable.tinyHomes[${index}].propertyDetails.accountLocFie.displayName`);
        }

        if (offeringCode === 'PerProInSto') {
            return getProInStoAccordianTitle(index)
        }

        return undefined
    }, [getProInStoAccordianTitle, offeringCode, submissionVM.value])

    const coveragesOverrides = useMemo(() => {
        const informationList = _.get(submissionVM, 'lobData.perLine.lineCoverages.locRiskCoverages.pprcoverages', []);
        let overrides = [];

        if (!_.isEmpty(informationList)) {
            overrides = informationList?.children.map((information, index) => ({
                [`locRiskCoverageComponent${index}`]: {
                    wizardData: submissionVM,
                    updateWizardData,
                    clausesBasePath: `lobData.perLine.lineCoverages.locRiskCoverages.pprcoverages.children[${index}].coverages`,
                    title: messages.cov,
                    clasueType: 'coverages',
                    pathToGet: `perLine.locRiskCoverages.pprcoverages[${index}].coverages`
                },
                [`locRiskCoveragesAccordionDetails${index}`]: {
                    title: getTitleofAccordion(index),
                    className: styles.gwOuterSpecialtyPersonalAccordion
                },
                [`locRiskCoveragesAccordion${index}`]: {
                    className: styles.gwAccordionSet
                },
                [`additionalInterestComponent${index}`]: {
                    submissionVM,
                    updateWizardData,
                    pathUpdated: `${offPath}.children[${index}].additionalInterest`,
                },
            }));
        }

        return Object.assign({}, ...overrides);
    }, [getTitleofAccordion, offPath, submissionVM, updateWizardData]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const overrideProps = {
        pprLineCoverageComponent: {
            wizardData: submissionVM,
            updateWizardData,
            clausesBasePath: `lobData.perLine.lineCoverages.coverages`,
            title: messages.cov,
            clasueType: 'coverages',
            pathToGet: 'perLine.coverages',
            visible: offeringCode === "PerUmbLia"
        },
        ...coveragesOverrides
    };

    const resolveCallbackMap = {
        ...callbackMap
    };

    const readValue = useCallback(
        (id, path) => readViewModelValue(metadata.content, submissionVM, id, path, overrideProps),
        [metadata.content, submissionVM, overrideProps]
    );

    const resolveComponentMap = {
        coveragespagecomponent: CoveragesPageComponent,
        additionalInterestComponent: AdditionalInterestComponent
    };

    return (
        <WizardPage
            skipWhen={() => initialValidation}
            disableNext = {!_.get(submissionVM,`${offPath}`).aspects.subtreeValid}
            onNext={onNext}
            onSave={onNext}
        >
            <ViewModelForm
                uiProps={metadata.content}
                model={submissionVM}
                onModelChange={updateWizardData}
                callbackMap={resolveCallbackMap}
                resolveValue={readValue}
                overrideProps={overrideProps}
                onValidationChange={onValidate}
                componentMap={resolveComponentMap}
                classNameMap={styles}
            />
        </WizardPage>
    );
}

LocRiskCoveragesPage.propTypes = wizardProps;
export default withViewModelService(LocRiskCoveragesPage);
